/**
 * Hook: AI transfer history summary generation.
 *
 * Manages the loading/error/result state for calling the AI transfer history
 * summary bot from the transfer modal.
 */

import { useCallback, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import {
  requestTransferHistorySummary,
  type TransferSummaryBotOutput,
} from '../../../lib/aiTransferHistorySummaryService';

export interface UseGenerateTransferSummaryParams {
  patientId: string;
  episodeOfCareId: string;
  currentPractitionerId: string;
}

export interface UseGenerateTransferSummaryResult {
  generate: () => Promise<TransferSummaryBotOutput | null>;
  isGenerating: boolean;
  result: TransferSummaryBotOutput | null;
  error: string | null;
  reset: () => void;
}

export function useGenerateTransferSummary(
  params: UseGenerateTransferSummaryParams | null,
): UseGenerateTransferSummaryResult {
  const medplum = useMedplum();
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<TransferSummaryBotOutput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef(false);

  const generate = useCallback(async (): Promise<TransferSummaryBotOutput | null> => {
    if (!params || isGenerating) return null;
    abortRef.current = false;
    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const output = await requestTransferHistorySummary(medplum, {
        patientId: params.patientId,
        episodeOfCareId: params.episodeOfCareId,
        currentPractitionerId: params.currentPractitionerId,
      });

      if (abortRef.current) return null;
      setResult(output);
      return output;
    } catch (err) {
      if (abortRef.current) return null;
      const message = err instanceof Error ? err.message : 'Failed to generate AI summary';
      setError(message);
      return null;
    } finally {
      if (!abortRef.current) {
        setIsGenerating(false);
      }
    }
  }, [medplum, params, isGenerating]);

  const reset = useCallback(() => {
    abortRef.current = true;
    setIsGenerating(false);
    setResult(null);
    setError(null);
  }, []);

  return { generate, isGenerating, result, error, reset };
}
